import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser, updateProfile as updateProfileApi } from '../services/api';
import { getTelegramWebApp } from '../utils/telegram';
import { readOffline, writeOffline, sessionFingerprint } from '../utils/offlineCache';

const UserContext = createContext();

// eslint-disable-next-line react-refresh/only-export-components
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('polka_token');
    if (user?.id && token) writeOffline('profile', sessionFingerprint(token), user);
  }, [user]);
  
  // Некоторые эндпоинты могут вернуть пользователя как { user: {...} }.
  // Нормализуем ответ, чтобы в контексте всегда был плоский объект профиля.
  const extractUser = (payload) => {
    if (!payload) return null;

    const looksLikeUser = (candidate) =>
      !!candidate &&
      typeof candidate === 'object' &&
      !Array.isArray(candidate) &&
      ('id' in candidate || 'username' in candidate || 'email' in candidate || 'name' in candidate);

    if (looksLikeUser(payload)) return payload;
    if (Array.isArray(payload)) {
      const first = payload.find(looksLikeUser);
      return first || null;
    }

    const nestedKeys = ['user', 'data', 'profile', 'result'];
    for (const key of nestedKeys) {
      if (looksLikeUser(payload[key])) {
        return payload[key];
      }
    }

    return null;
  };

  // Загружаем пользователя с бэкэнда при инициализации или изменении токена
  useEffect(() => {
    // TelegramAuthGate establishes the verified session before mounting app data.
    if (getTelegramWebApp()) { setLoading(false); return; }
    const loadUser = async () => {
      const token = localStorage.getItem('polka_token');
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        const rawUserData = await getCurrentUser();
        if (localStorage.getItem('polka_token') !== token) return;
        const userData = extractUser(rawUserData);
        if (!userData?.id) {
          throw new Error('Некорректный формат ответа пользователя');
        }
        setUser(userData);
        writeOffline('profile', sessionFingerprint(token), userData);
      } catch (err) {
        console.error('Ошибка загрузки пользователя:', err);
        if (err.status === 401 && localStorage.getItem('polka_token') === token) {
          localStorage.removeItem('polka_token');
          setUser(null);
        } else if ((!err.status || err.status >= 500) && localStorage.getItem('polka_token') === token) {
          const cached = readOffline('profile', sessionFingerprint(token), null);
          if (cached?.id) setUser(cached);
        }
      } finally {
        setLoading(false);
      }
    };

    loadUser();

    // Подписываемся на изменения в localStorage
    const handleStorageChange = (e) => {
      if (e.key === 'polka_token') {
        loadUser();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Функция для принудительной перезагрузки пользователя
  const refreshUser = async () => {
    const token = localStorage.getItem('polka_token');
    if (!token) {
      setUser(null);
      return;
    }

    try {
      setLoading(true);
      const rawUserData = await getCurrentUser();
      if (localStorage.getItem('polka_token') !== token) return;
      const userData = extractUser(rawUserData);
      if (!userData?.id) {
        throw new Error('Некорректный формат ответа пользователя');
      }
      setUser(userData);
    } catch (err) {
      console.error('Ошибка перезагрузки пользователя:', err);
      if (err.status === 401 && localStorage.getItem('polka_token') === token) {
        localStorage.removeItem('polka_token');
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // Обновить профиль пользователя
  const updateProfile = async (updates) => {
    try {
      const rawUpdatedUser = await updateProfileApi(updates);
      const updatedUser = extractUser(rawUpdatedUser);
      setUser(updatedUser);
      return updatedUser;
    } catch (err) {
      console.error('Ошибка обновления профиля:', err);
      throw err;
    }
  };

  // Обновить аватар
  const updateAvatar = (avatarUrl) => {
    setUser(prev => ({
      ...prev,
      avatar: avatarUrl,
      updatedAt: new Date().toISOString()
    }));
  };

  // Обновить статистику
  const updateStats = (statsUpdates) => {
    setUser(prev => ({
      ...prev,
      stats: {
        ...prev.stats,
        ...statsUpdates
      },
      updatedAt: new Date().toISOString()
    }));
  };

  // Выйти из системы
  const logout = () => {
    localStorage.removeItem('polka_token');
    setUser(null);
  };

  return (
    <UserContext.Provider value={{
      user,
      setUser,
      loading,
      error,
      updateProfile,
      updateAvatar,
      updateStats,
      logout,
      refreshUser
    }}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
